<template>
  <!-- Escape closes the panel from anywhere inside the card, including from a
       field that has focus. -->
  <div class="kf-filter neumorphism-flat" @keydown.esc="closePanel">
    <!--
      THE COMPACT ROW. One line, 52px tall, and the only thing on screen when
      the panel is shut.

      It used to be two stacked rows -- a "Filters" title with the buttons on
      the right, then a four-column grid of labelled controls -- which came to
      ~130px before the table could start. The title said nothing a search box
      and a "More filters" button do not already say, so it is gone and the
      buttons moved up beside the controls.

      Only Search and Date range live here by default; a screen can add its own
      with `pinned: true`. Pinned controls carry no label above them -- their
      placeholder does that job -- because a label is what makes a row 60px.
    -->
    <div class="kf-bar">
      <!-- Search is local to the screen on purpose. The legacy navbar search
           writes to shared Vuex state and is cleared on sidebar tab change,
           which would make these screens behave unpredictably. -->
      <div v-if="showSearch" class="kf-bar__search">
        <v-text-field
          :value="search"
          :placeholder="searchPlaceholder"
          :aria-label="searchPlaceholder"
          prepend-inner-icon="mdi-magnify"
          hide-details
          dense
          outlined
          single-line
          clearable
          @input="onSearch"
          @click:clear="onSearch('')"
          @keydown.enter="onSearchNow"
        />
      </div>

      <div
        v-if="showDate"
        class="kf-bar__date"
        :class="{ 'kf-bar__date--titled': dateTitle }"
      >
        <v-menu
          v-model="date_menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <!-- `dateTitle` names the box when a screen has a second date
                 filter beside it (Leads: Applied on + Disbursement date); a
                 lone date box needs no name and stays single-line. -->
            <v-text-field
              :value="dateLabel"
              :label="dateTitle || undefined"
              :aria-label="dateTitle || 'Date range'"
              prepend-inner-icon="mdi-calendar"
              readonly
              hide-details
              dense
              outlined
              :single-line="!dateTitle"
              :clearable="hasDate"
              v-bind="attrs"
              v-on="on"
              @click:clear="clearDate"
            />
          </template>
          <v-date-picker
            v-model="local_range"
            range
            no-title
            color="primary"
            @change="onDateChange"
          />
        </v-menu>
      </div>

      <!-- Schema fields a screen chose to keep out of the panel. -->
      <div
        v-for="f in pinnedFilters"
        :key="f.key"
        class="kf-bar__pinned"
        :class="{ 'kf-bar__pinned--date': f.type === 'date-range' }"
      >
        <!-- A pinned range matches the date box above, label and all -->
        <BarDateRange
          v-if="f.type === 'date-range'"
          :value="value[f.key] || []"
          :title="f.label"
          :max="f.max"
          @input="value[f.key] = $event"
          @change="onFieldChange(f)"
        />
        <FilterField
          v-else
          :config="f"
          :model="value"
          compact
          @change="onFieldChange"
        />
      </div>

      <!-- Everything after this is pushed to the right-hand end. -->
      <div class="kf-bar__spacer"></div>

      <slot name="actions"></slot>

      <!-- Rendered only when there is something in the panel to reveal, so a
           screen with one date control gets no button. -->
      <v-btn
        v-if="hasToggle"
        text
        small
        class="text-none kf-bar__btn"
        :aria-expanded="String(advanced_open)"
        :aria-controls="panelId"
        @click="advanced_open = !advanced_open"
      >
        {{ advanced_open ? "Less filters" : "More filters" }}
        <span v-if="appliedCount" class="kf-filter__badge ml-2">{{ appliedCount }}</span>
        <LucideIcon
          :name="advanced_open ? 'chevron-up' : 'chevron-down'"
          :size="15"
          class="ml-1"
        />
      </v-btn>

      <v-btn
        v-if="showReset"
        text
        small
        class="text-none kf-bar__btn"
        @click="onReset"
      >
        <LucideIcon name="refresh-cw" :size="14" class="mr-1" />
        Reset
      </v-btn>

      <!-- How many rows the filters above actually left. Muted, so it reads as
           a readout rather than another control. -->
      <span v-if="hasResultCount" class="kf-bar__count">
        {{ fmt.number(resultCount) }}<template v-if="resultLabel"> {{ resultLabel }}</template>
      </span>
    </div>

    <!--
      THE PANEL. Separated from the row above by a real rule and a faint tint,
      because it IS a second area now -- unlike the old single grid, where the
      toggle only changed how much of one grid was showing.
    -->
    <div v-show="advanced_open" :id="panelId" class="kf-panel">
      <div class="kf-fgrid" :class="{ 'kf-fgrid--tall': isTall }">
        <!-- The slot form, still supported: 19 screens pass their controls as
             children, and they render on the same grid tracks as the schema
             ones. Both slots sit inside the panel now, so the compact row stays
             one line however many filters a screen has.
             Ahead of the schema fields because a screen that uses both is one
             whose filter state is split -- Leads keeps Loan type and Product as
             component data and only its six UTM arrays in one object -- and the
             hand-written pair are the ones an operator reaches for first. -->
        <slot></slot>

        <FilterField
          v-for="f in panelFilters"
          :key="f.key"
          :config="f"
          :model="value"
          @change="onFieldChange"
        >
          <!-- A control the schema cannot express -- an async autocomplete, an
               infinite-scroll picker -- comes in through a per-field slot. -->
          <template #field>
            <slot :name="'field-' + f.key" :config="f"></slot>
          </template>
        </FilterField>

        <div class="kf-filter__rest">
          <slot name="advanced"></slot>
        </div>
      </div>
    </div>

    <!-- Chips say why the table is short, without the panel having to be open.
         Schema screens get them for free; the slot overrides for the rest. -->
    <div v-if="hasChips" class="kf-filter__chips">
      <slot name="chips">
        <v-chip
          v-for="c in chipList"
          :key="c.key"
          small
          close
          class="kf-fchip"
          @click:close="clearField(c.key)"
        >
          <strong>{{ c.label }}:</strong>&nbsp;{{ c.value }}
        </v-chip>
        <button
          v-if="chipList.length > 1"
          type="button"
          class="kf-fchip__clear"
          @click="onReset"
        >
          Clear all
        </button>
      </slot>
    </div>
  </div>
</template>

<script>
import fmt from "@/app-js/karrofin/format";
import FilterField from "./FilterField.vue";
import BarDateRange from "./BarDateRange.vue";

let uid = 0;

/**
 * One filter, described rather than hand-written.
 *
 * @typedef  {Object} FilterConfig
 * @property {string}  key             key on the filter-state object; also the
 *                                     chip's key and what `clearField` resets
 * @property {string}  label           label above the control, and on its chip
 * @property {'select'|'multiselect'|'date-range'|'text'|'number'} type
 * @property {Array}   [options]       items for select / multiselect
 * @property {string}  [itemText]      option label field (default: plain value)
 * @property {string}  [itemValue]     option value field
 * @property {boolean} [returnObject]  bind the whole option, not its value
 * @property {string}  [placeholder]   default "Any"
 * @property {*}       [defaultValue]  what Reset and a chip's x restore;
 *                                     inferred ([] or null) when absent
 * @property {boolean} [pinned]        keep in the compact row, label-less
 * @property {boolean} [slot]          render `#field-<key>` instead -- the
 *                                     escape hatch for async autocompletes and
 *                                     infinite-scroll pickers
 * @property {(filters:Object)=>boolean} [show]  hide when false; the grid
 *                                     closes up rather than leaving a hole
 */

/**
 * ---------------------------------------------------------------------------
 * MIGRATING A SCREEN TO THE SCHEMA FORM
 * ---------------------------------------------------------------------------
 * Nothing below is required -- the slot form still works and is what most
 * screens use. Convert when a screen's filter template is mostly a chain of
 * `v-if="f.key === '...'"`.
 *
 * Before (~90 lines of template + ~60 of computed):
 *
 *   <FilterBar :advanced-count="activeChips.length" :rest-count="restCount" ...>
 *     <template v-for="f in filterLayout">
 *       <div :key="f.key" class="kf-field" :class="{'kf-field--rest': f.rest}">
 *         <span class="kf-field__label">{{ f.label }}</span>
 *         <MultiSelect v-if="f.key === 'city'" v-model="filters.city" ... />
 *         <v-select v-else-if="f.key === 'age'" ... />
 *         ...
 *       </div>
 *     </template>
 *     <template #chips> ...hand-written chips... </template>
 *   </FilterBar>
 *
 * After:
 *
 *   <FilterBar
 *     :filters="filterSchema"
 *     :value="filters"
 *     :search.sync="search_query"
 *     :result-count="total_count"
 *     result-label="users"
 *     @change="reload()"
 *     @reset="resetFilters"
 *   />
 *
 *   filterSchema() {
 *     return [
 *       { key: "city",  label: "City",  type: "multiselect", options: this.options.cities },
 *       { key: "age",   label: "Age",   type: "select", options: this.options.age_bands,
 *         itemText: "name", itemValue: "value" },
 *     ];
 *   }
 *
 * `activeChips`, `filterLayout` and `restCount` are then all derived here and
 * can be deleted from the screen. `@reset` still fires, so a screen keeps
 * anything the schema cannot know about (page_number, a dependent field).
 *
 * The state object passed as `value` is mutated in place -- the same thing
 * every screen already does to its Vuex filter module -- so no `input`
 * handler is needed on the parent.
 */
export default {
  name: "KfFilterBar",
  components: { FilterField, BarDateRange },
  props: {
    /** Filter schema. Omit to use the slot form. @type {FilterConfig[]} */
    filters: { type: Array, default: () => [] },
    /** The screen's filter-state object, mutated in place. */
    value: { type: Object, default: null },
    /** Rows the current filters left, shown as a muted readout. */
    resultCount: { type: Number, default: null },
    /** Noun for the readout, e.g. "users". */
    resultLabel: { type: String, default: "" },

    search: { type: String, default: "" },
    dateRange: { type: Array, default: () => [] },
    /** Names the built-in date box; omit for a screen with one date filter. */
    dateTitle: { type: String, default: "" },
    searchPlaceholder: { type: String, default: "Search name, mobile or ID" },
    showSearch: { type: Boolean, default: true },
    showDate: { type: Boolean, default: true },
    showReset: { type: Boolean, default: true },
    /** Applied-filter count for SLOT screens; derived when `filters` is given. */
    advancedCount: { type: Number, default: 0 },
    /**
     * Slot-form screens pass how many of their fields are overflow. It no
     * longer hides anything -- every slot field is in the panel now -- but it
     * still tells the bar there is something to reveal, and drives the height
     * cap on the screens with nineteen filters.
     */
    restCount: { type: Number, default: 0 },
  },
  data() {
    return {
      fmt,
      date_menu: false,
      // Always collapsed on arrival. The chip row already says which filters
      // are applied, so an auto-opened panel would be explaining something the
      // card has already said.
      advanced_open: false,
      local_range: this.dateRange.slice(),
      debounce_timer: null,
      panelId: "kf-filter-panel-" + ++uid,
    };
  },
  computed: {
    /** A screen is in schema mode once it passes both a schema and a state. */
    schemaMode() {
      return this.filters.length > 0 && !!this.value;
    },
    /** Schema fields whose `show` gate passes. */
    visibleFilters() {
      if (!this.schemaMode) return [];
      return this.filters.filter((f) => !f.show || f.show(this.value));
    },
    pinnedFilters() {
      return this.visibleFilters.filter((f) => f.pinned);
    },
    panelFilters() {
      return this.visibleFilters.filter((f) => !f.pinned);
    },
    hasDefault() {
      // Content, not just presence. A screen can declare a slot and have
      // everything inside it v-if away -- Vue 2 leaves a comment placeholder
      // behind, which a truthiness test happily counts.
      return this.slotHasContent("default");
    },
    hasAdvanced() {
      return this.slotHasContent("advanced");
    },
    /** Anything in the panel earns the More/Less button. */
    hasToggle() {
      if (this.schemaMode) return this.panelFilters.length > 0;
      return this.hasAdvanced || this.hasDefault || this.restCount > 0;
    },
    /**
     * Whether opening the panel would push the table off the bottom.
     *
     * Pre-Verification and the Business Loan list have nineteen filters and
     * Applications eighteen, so past two rows (four columns, so eight fields)
     * the panel scrolls within itself instead of growing the page.
     */
    isTall() {
      const count = this.schemaMode ? this.panelFilters.length : this.restCount;
      return count > 8;
    },
    /** One entry per applied schema filter. */
    chipList() {
      if (!this.schemaMode) return [];
      const out = [];
      this.visibleFilters.forEach((f) => {
        const chip = this.chipFor(f);
        if (chip) out.push(chip);
      });
      return out;
    },
    /**
     * A screen that draws its own chips owns the count too.
     *
     * The KarroFin screens derive both from `app-js/karrofin/filters.js`, whose
     * whole point is that `toParams()` is the single writer of filter params --
     * so their `activeFilters()` result is the authority on what is applied,
     * not anything this component could infer from a render schema. Deriving it
     * here as well would be a second opinion that could disagree.
     */
    ownsChips() {
      return this.slotDeclared("chips");
    },
    appliedCount() {
      return this.ownsChips ? this.advancedCount : this.chipList.length;
    },
    hasChips() {
      // Slot presence is not enough: screens that use it always declare it, so
      // an empty one rendered a bare strip.
      if (this.ownsChips) return this.advancedCount > 0;
      return this.chipList.length > 0;
    },
    hasResultCount() {
      return this.resultCount !== null && this.resultCount !== undefined;
    },
    hasDate() {
      return !!(this.dateRange && this.dateRange.length === 2);
    },
    dateLabel() {
      const r = this.dateRange;
      if (!r || r.length !== 2) return "All dates";
      // The compact form: "20 May - 18 Aug 2026" rather than
      // "20-05-2026 -> 18-08-2026", which was being clipped on every screen.
      return fmt.dateRangeShort(r[0], r[1]);
    },
  },
  watch: {
    dateRange(value) {
      this.local_range = (value || []).slice();
    },
  },
  methods: {
    slotDeclared(name) {
      return !!(this.$slots[name] || this.$scopedSlots[name]);
    },
    slotHasContent(name) {
      // A scoped slot is a function, so it cannot be inspected without calling
      // it; treat it as present, since a screen that bothers to pass one is
      // passing something.
      if (this.$scopedSlots[name]) return true;
      const nodes = this.$slots[name] || [];
      return nodes.some((n) => n.tag || (n.text && n.text.trim()));
    },
    /** The value a Reset or a chip's x restores this field to. */
    emptyValue(config) {
      if (Object.prototype.hasOwnProperty.call(config, "defaultValue")) {
        return typeof config.defaultValue === "object" &&
          config.defaultValue !== null
          ? JSON.parse(JSON.stringify(config.defaultValue))
          : config.defaultValue;
      }
      if (config.type === "multiselect" || config.type === "date-range") return [];
      return null;
    },
    /** Human-readable value for one applied filter, or null when it is unset. */
    chipFor(config) {
      const raw = this.value[config.key];
      if (raw === null || raw === undefined || raw === "") return null;

      if (config.type === "date-range") {
        if (!Array.isArray(raw) || raw.length !== 2 || !raw[0] || !raw[1]) return null;
        return {
          key: config.key,
          label: config.label,
          value: fmt.dateRangeShort(raw[0], raw[1]),
        };
      }
      if (config.type === "multiselect") {
        if (!Array.isArray(raw) || !raw.length) return null;
        return {
          key: config.key,
          label: config.label,
          value:
            raw.length === 1
              ? this.optionLabel(config, raw[0])
              : raw.length + " selected",
        };
      }
      // An object-valued select (return-object) carries its own label.
      if (typeof raw === "object") {
        const text = config.itemText ? raw[config.itemText] : raw.name;
        if (!text) return null;
        return { key: config.key, label: config.label, value: text };
      }
      return {
        key: config.key,
        label: config.label,
        value: this.optionLabel(config, raw),
      };
    },
    /** Maps a stored value back to the option text a user picked. */
    optionLabel(config, raw) {
      const items = config.options || [];
      const match = items.find((o) => {
        if (o === null || typeof o !== "object") return o === raw;
        return config.itemValue ? o[config.itemValue] === raw : false;
      });
      if (match && typeof match === "object") {
        return config.itemText ? match[config.itemText] : String(raw);
      }
      return String(raw);
    },
    /**
     * A field changed.
     *
     * `onChange` on the config is the escape hatch for a screen whose state is
     * not a plain object -- the KarroFin screens replace their whole `filters`
     * object through `clearOne`/`defaultFilters` so the contract module stays
     * the only writer. When a config supplies one it OWNS the reload, so this
     * does not also emit `change`.
     *
     * `clears` is filters.js's own vocabulary, honoured here for screens that
     * do let the bar manage state: picking a KYC document drops the status left
     * over from the previous one, which would otherwise keep narrowing the
     * table under a control the operator thinks they just changed.
     */
    onFieldChange(config) {
      if (config && typeof config.onChange === "function") {
        config.onChange(this.value[config.key], this.value);
        return;
      }
      if (config && config.clears && !this.ownsChips) {
        config.clears.forEach((key) => {
          const dependent = this.filters.find((f) => f.key === key);
          if (dependent) this.value[key] = this.emptyValue(dependent);
        });
      }
      this.$emit("change");
    },
    /** One chip's x. */
    clearField(key) {
      const config = this.filters.find((f) => f.key === key);
      if (!config) return;
      this.value[key] = this.emptyValue(config);
      this.$emit("change");
    },
    /**
     * Clears the schema fields, then hands off. The screen's own `@reset` still
     * runs, so anything the schema cannot know about -- page_number, a
     * dependent field, a default date window -- is still its job.
     */
    onReset() {
      // Same division as the chips: a screen that draws its own owns clearing
      // too. The KarroFin screens rebuild their state from the contract module
      // (`defaultFilters`), which knows the real empty value for every kind --
      // clearing the keys here first would just be overwritten a tick later.
      if (this.schemaMode && !this.ownsChips) {
        this.filters.forEach((f) => {
          this.value[f.key] = this.emptyValue(f);
        });
      }
      this.$emit("reset");
    },
    // 350ms debounce so typing does not fire a request per keystroke, matching
    // the legacy navbar's 500ms feel without being sluggish.
    onSearch(value) {
      clearTimeout(this.debounce_timer);
      this.debounce_timer = setTimeout(() => {
        this.$emit("update:search", value || "");
        this.$emit("change");
      }, 350);
    },
    /** Enter searches at once rather than waiting out the debounce. */
    onSearchNow(event) {
      clearTimeout(this.debounce_timer);
      this.$emit("update:search", event.target.value || "");
      this.$emit("change");
    },
    closePanel() {
      this.advanced_open = false;
    },
    clearDate() {
      this.local_range = [];
      this.$emit("update:dateRange", []);
      this.$emit("change");
    },
    onDateChange() {
      if (this.local_range.length !== 2) return;
      // v-date-picker can hand back the range reversed if the user picks the
      // later date first.
      const sorted = this.local_range.slice().sort();
      this.date_menu = false;
      this.$emit("update:dateRange", sorted);
      this.$emit("change");
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounce_timer);
  },
};
</script>

<style scoped>
.kf-filter {
  background: var(--kf-surface);
  border-radius: var(--kf-radius);
  margin-bottom: 14px;
}

/* ----------------------------------------------------------- compact row -- */
/*
 * 8px + a 36px control + 8px = 52px. The row is the whole of the card when the
 * panel is shut, so this number IS the height of the filter area.
 *
 * `flex-wrap` rather than overflow: below ~768px, or on a screen that pins
 * extra controls, the row becomes two lines instead of clipping the Reset
 * button off the right-hand edge.
 */
.kf-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 16px;
}
.kf-bar__search {
  flex: 1 1 240px;
  min-width: 240px;
  max-width: 420px;
}
.kf-bar__date {
  flex: 0 0 190px;
}
.kf-bar__pinned {
  flex: 0 0 170px;
}
/* Two named ranges side by side (Leads): wide enough for "14 Sep - 14 Oct
   2026" beside the calendar and clear icons with room to spare -- at 215px
   the year ran right up against the clear button. */
.kf-bar__date--titled,
.kf-bar__pinned--date {
  flex-basis: 230px;
}
/* Pushes the buttons and the readout to the right-hand end, and collapses to
   nothing once the row wraps, so they sit under the search rather than
   stranded on a line of their own. */
.kf-bar__spacer {
  flex: 1 1 0;
  min-width: 0;
}
/*
 * Vuetify ships `.v-btn { letter-spacing: .0892857143em; text-indent: .0892857143em }`
 * and `.v-size--small { font-size: .75rem }`, and nothing reset it -- so these
 * buttons rendered at 12px with ~1.25px of tracking next to 13px controls with
 * normal tracking, and read as a different typeface.
 *
 * `!important` because Vuetify's own `.v-btn` rules tie these on specificity,
 * and its CSS is code-split so it can land after this sheet in the production
 * build.
 */
.kf-bar__btn {
  font-family: inherit !important;
  font-size: var(--kf-fs-meta) !important;
  font-weight: 700 !important;
  letter-spacing: normal !important;
  text-indent: 0 !important;
  color: var(--kf-brand) !important;
  flex: 0 0 auto;
}
.kf-bar__count {
  flex: 0 0 auto;
  font-size: var(--kf-fs-meta);
  font-weight: 700;
  color: var(--kf-text-muted, #64748b);
  background: var(--kf-control);
  border-radius: 999px;
  padding: 4px 12px;
  white-space: nowrap;
}

/* ----------------------------------------------------------------- panel -- */
/* A real rule and a tint, because this IS a second area -- unlike the old
   single grid, where the toggle only changed how much of one grid showed. */
.kf-panel {
  border-top: 1px solid var(--kf-border);
  background: var(--kf-surface-2);
  padding: 14px 16px;
  border-radius: 0 0 var(--kf-radius) var(--kf-radius);
}

/* ----------------------------------------------------------------- chips -- */
.kf-fchip__clear {
  font-size: var(--kf-fs-meta);
  font-weight: 700;
  color: var(--kf-brand);
  margin-left: 4px;
}
.kf-fchip__clear:hover {
  text-decoration: underline;
}
</style>

<style>
/* --------------------------------------------------------------- fields --- */
/* A panel filter is a label ABOVE a box.

   Vuetify's own floating label was the source of the mismatch this replaces:
   an outlined field with a label draws a *notched* outline with the value
   pushed down, one without draws an unbroken rectangle with the value centred.
   The bar had both, so its controls sat at different heights and read as
   different components. With the label lifted out, every control is the same
   unbroken box -- and the label stays full size and readable once a value is
   chosen, instead of shrinking into the border. */
.kf-filter .kf-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.kf-filter .kf-field__label {
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
  white-space: nowrap;
}

/* One height for every control in the bar. 36px rather than the 40px
   `--kf-control-h`, which is what buys the 52px row.
   `!important` because Vuetify's own dense rule is equally specific -- without
   it this wins only on source order, which the code-split production build can
   flip silently. */
.kf-filter .v-input--dense .v-input__slot {
  min-height: 36px !important;
  background: var(--kf-surface) !important;
}
/* The search box sits on the white row, so it takes the tinted fill instead --
   it is the one control that should read as an input before it is clicked. */
.kf-bar__search .v-input--dense .v-input__slot {
  background: var(--kf-surface-2) !important;
}
/* Panel controls sit on the tinted panel, so they invert back to white. */
.kf-panel .v-input--dense .v-input__slot {
  background: var(--kf-surface) !important;
}
.kf-filter .v-input__prepend-inner {
  margin-top: 6px !important;
}
/* 13px. Vuetify's 16px default is what pushed the date value past its box. */
.kf-filter input,
.kf-filter .v-select__selection,
.kf-filter .v-select__selection--comma {
  font-size: var(--kf-fs-meta);
}
/* single-line keeps Vuetify from reserving room for a floating label it no
   longer has; without this the box is 52px and the value sits low. */
.kf-filter .v-text-field--outlined.v-input--dense .v-input__control > .v-input__slot {
  align-items: center;
}
.kf-filter .v-text-field--outlined .v-input__append-inner {
  margin-top: 6px !important;
}
/* A named date box's label, centred on the top border. Vuetify lifts a
   dense outlined label 16px, which is right for its 40px box; these are 36px
   with the contents centred, so it sat 3px low and ran into the calendar
   icon. Only the named boxes (Leads' two ranges) have a label at all.
   `!important` for the same reason as the slot height above. */
.kf-filter .kf-bar__date--titled .v-label--active,
.kf-filter .kf-bar__pinned--date .v-label--active {
  transform: translateY(-19px) scale(0.75) !important;
}

/* Sized by the grid track, like every other control in the bar. */
.kf-filter .kf-select {
  min-width: 0;
}
/* One edge for every control. Vuetify draws its outline at rgba(0,0,0,.38) --
   a mid grey heavier than any other line on the page -- and darkens it to
   rgba(0,0,0,.86), near black, on hover, so whichever box the cursor last
   crossed reads as selected. Both go through the design system's own border
   tokens instead, which is what makes a row of controls read as one set. */
.kf-filter .v-text-field--outlined fieldset {
  border-color: var(--kf-border);
}
.kf-filter .v-text-field--outlined:hover fieldset {
  border-color: var(--kf-border-strong);
}
.kf-filter .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--kf-brand);
}

/* ----------------------------------------------------------------- grid --- */
/*
 * Four fixed columns, not `auto-fill`. Every panel filter -- schema-driven and
 * slot-supplied alike -- is a direct child, so the panel is one scannable
 * block: same width, same height, same spacing, nothing drawn between them.
 *
 * `minmax(0, 1fr)` rather than `1fr` so a long select value cannot blow its
 * track out and push the row wider than the card.
 */
.kf-filter .kf-fgrid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 16px;
  align-items: end;
}
/* The legacy drawer is permanent and takes ~256px, so these are set from what
   is left for the card, not from the viewport: 4 columns down to 3, then 2,
   then a single stack once a second column would be under ~200px. */
@media (max-width: 1280px) {
  .kf-filter .kf-fgrid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 960px) {
  .kf-filter .kf-fgrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 768px) {
  .kf-filter .kf-fgrid {
    grid-template-columns: 1fr;
  }
}
/* Inside the grid the track sets the width, so any flex sizing a field carries
   has to stand down -- otherwise max-width leaves ragged gaps mid-row. */
.kf-filter .kf-fgrid .kf-field {
  flex: initial;
  max-width: none;
}

/*
 * The cap for a screen with a lot of filters -- see `isTall`.
 *
 * 40vh leaves the table's first rows on screen with the panel fully open, and
 * the overflow scrolls inside the card. `padding-right` keeps the scrollbar off
 * the right-hand column's controls; `padding-top` stops the focus ring on a
 * first-row field being clipped by the scroll container's edge.
 */
.kf-filter .kf-fgrid--tall {
  max-height: 40vh;
  overflow-y: auto;
  padding-top: 2px;
  padding-right: 4px;
}

/* The wrapper exists only to hold the `advanced` slot -- it must not become a
   grid cell itself, or its controls would stack inside one 300px column.
   `contents` removes the box and promotes its children to grid items, so they
   land on the same four tracks as the fields above them. */
.kf-filter__rest {
  display: contents;
}

/* ----------------------------------------------------------------- chips --- */
.kf-filter .kf-filter__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 0 16px 12px;
}
/*
 * Buttons a screen passes into the `actions` slot -- "Eligibility rejection
 * count", "Export CSV", "Create New Product".
 *
 * These have to be unscoped. Slot content is compiled in the PARENT's scope,
 * so it carries the page's data-v attribute and never the bar's: a scoped rule
 * here could never reach it, which is why these buttons have been rendering as
 * plain Vuetify text buttons -- 12px, uppercase tracking, default colour --
 * beside 13px brand-coloured ones.
 */
.kf-filter .kf-filter__reset,
.kf-filter .kf-filter__more {
  font-family: inherit !important;
  font-size: var(--kf-fs-meta) !important;
  font-weight: 700 !important;
  letter-spacing: normal !important;
  text-indent: 0 !important;
  color: var(--kf-brand) !important;
}

/* `.kf-fchip` itself is styled once in app-css/kf-tokens.css -- it is shared
   with screens that draw their own chips, so it is not repeated here. */
.kf-filter .kf-filter__badge {
  display: inline-block;
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--kf-brand);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
</style>
